/* Basket drawer — Phase 6d.
 *
 * The script never builds HTML. Every refresh re-fetches the drawer
 * section through Shopify's Section Rendering API and swaps its innards,
 * so the markup contract stays in cart-drawer.liquid and cart-line.liquid.
 *
 * Everything it intercepts already works without it:
 *   · add-to-cart is a real POST to /cart/add
 *   · − / + / Remove are real links to /cart/change
 *   · Checkout is a real submit to /cart
 * so a failure here degrades to full page loads rather than to nothing.
 *
 * No storage of any kind (CLAUDE.md §2.8).
 */
(function () {
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer || typeof drawer.showModal !== 'function') return;

  const countEls = document.querySelectorAll('[data-cart-count]');
  const root = () => window.Shopify?.routes?.root || '/';

  const open = () => {
    if (!drawer.open) drawer.showModal();
  };

  /* No Accept header here, deliberately. Verified 2026-08-17: sending
   * `Accept: application/json` to a product or cart URL makes Shopify serve
   * that resource's own .js JSON instead of the Section Rendering API, so
   * `?sections=cart-drawer` came back as the whole product object and the
   * drawer silently kept its stale markup. */
  const refresh = async ({ show = false } = {}) => {
    const response = await fetch(`${root()}?sections=cart-drawer`);
    if (!response.ok) throw new Error('section fetch failed');
    const sections = await response.json();
    if (!sections['cart-drawer']) throw new Error('cart-drawer section missing');
    const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    const fresh = parsed.querySelector('[data-cart-drawer]');
    if (!fresh) throw new Error('cart-drawer markup missing');
    drawer.innerHTML = fresh.innerHTML;

    const cart = await (await fetch(`${root()}cart.js`)).json();
    countEls.forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });

    if (show) open();
  };

  // --- Add to basket -------------------------------------------------
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    event.preventDefault();

    const buttons = form.closest('.shopify-section')?.querySelectorAll('[data-add-button]') || [];
    const errorEl = form.querySelector('[data-buy-error]');
    buttons.forEach((button) => (button.disabled = true));
    if (errorEl) errorEl.hidden = true;

    try {
      const response = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.description || body.message || 'Could not add to basket.');
      await refresh({ show: true });
    } catch (error) {
      if (errorEl) {
        errorEl.textContent = error.message;
        errorEl.hidden = false;
      } else {
        form.submit(); // No slot to report into — let the real POST run.
        return;
      }
    } finally {
      buttons.forEach((button) => (button.disabled = false));
    }
  });

  // --- Quantity and remove, inside the drawer ------------------------
  drawer.addEventListener('click', async (event) => {
    if (event.target.closest('[data-cart-close]')) {
      drawer.close();
      return;
    }
    const control = event.target.closest('[data-line-change]');
    if (!control) return;
    event.preventDefault();
    await change(control.dataset.lineChange, control.dataset.lineQuantity);
  });

  drawer.addEventListener('change', async (event) => {
    const input = event.target.closest('[data-line-input]');
    if (!input) return;
    await change(input.dataset.lineInput, input.value);
  });

  const change = async (line, quantity) => {
    try {
      const response = await fetch(`${root()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Number(quantity) }),
      });
      if (!response.ok) throw new Error('change failed');
      await refresh();
    } catch {
      window.location.href = window.Shopify?.routes?.cart_url || '/cart';
    }
  };

  // --- Opening it ----------------------------------------------------
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cart-open]');
    if (!trigger) return;
    event.preventDefault();
    refresh({ show: true }).catch(() => {
      window.location.href = trigger.getAttribute('href') || '/cart';
    });
  });

  // Clicking the backdrop closes it; the dialog element handles Escape.
  drawer.addEventListener('click', (event) => {
    if (event.target === drawer) drawer.close();
  });
})();
